import { createEffect, createSignal, Show } from "solid-js";
import { cn } from "@/libs/cn";

/**
 * Up to two initials, one per word — "Novanta Labs" reads NL, "Google" reads G.
 * Falls back to a neutral mark rather than an empty box, because a company with
 * no name yet is a loading state, not a design.
 */
export const companyInitials = (name: string | null | undefined) => {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
  return initials || "?";
};

type CompanyMonogramProps = {
  name: string | null | undefined;
  logoUrl?: string | null;
  class?: string;
};

/**
 * The company logo, or its initials when there is none. Shared so that the
 * public page and the settings page cannot disagree on what a company without
 * a logo looks like.
 *
 * "Has none" includes **a URL that does not load**. The column holds addresses
 * the LinkedIn import wrote months ago, and a dead one used to render the
 * browser's broken-image glyph — the one thing this component exists to avoid,
 * on a page whose job is to make the company look worth writing to (EP5). The
 * failure is recorded here, inside the shared component, so no two call sites
 * can fall back differently.
 */
export const CompanyMonogram = (props: CompanyMonogramProps) => {
  const [failed, setFailed] = createSignal(false);

  // A new address deserves a new attempt: the settings page can replace a
  // broken logo while this component is mounted.
  //
  // Eccezione registrata al divieto di «effetto che scrive solo un altro
  // segnale». La regola presuppone che il valore sia derivabile, e qui non lo
  // è: `failed` dipende anche dall'`onError` dell'`<img>`, che è un fatto
  // esterno e non una funzione di `logoUrl`. Un `createMemo` non può osservarlo,
  // e togliere l'effetto reintrodurrebbe uno stale-UI vero — un logo sostituito
  // resterebbe «rotto» finché il componente non viene smontato.
  createEffect(() => {
    props.logoUrl;
    setFailed(false);
  });

  return (
    <Show
      when={props.logoUrl && !failed()}
      fallback={
        <div
          class={cn(
            "grid shrink-0 place-items-center rounded-xl bg-primary/10 font-display font-bold text-primary",
            props.class,
          )}
          aria-hidden="true"
        >
          {companyInitials(props.name)}
        </div>
      }
    >
      <img
        src={props.logoUrl ?? ""}
        alt={props.name ?? ""}
        class={cn("shrink-0 rounded-xl object-cover", props.class)}
        onError={() => setFailed(true)}
      />
    </Show>
  );
};
